<template>
  <div class="mx-auto mw-700">
    <v-spacer class="mt-2" />

    <v-row dense>
      <v-col>
        <h3>
          {{ $t('host.controlAccess.platformAccess') }}
        </h3>
        <p v-if="host.enabled">
          {{ $t('host.controlAccess.hostHasAccess') }}
        </p>
        <p v-else>
          {{ $t('host.controlAccess.hostHasNoAccess') }}
        </p>
        <v-btn
          @click="changeHostStatus"
          :color="host.enabled ? 'warning' : 'primary'"
          :loading="isLoadingChangeStatus"
          elevation="0"
        >
          <div style="transform: scale(0.85); opacity: 0.75">
            <v-icon v-if="host.enabled" left> mdi-close </v-icon>
            <v-icon v-else left> mdi-check </v-icon>
          </div>
          {{
            host.enabled
              ? $t('host.controlAccess.blockAccess')
              : $t('host.controlAccess.unblockAccess')
          }}
        </v-btn>
      </v-col>
      <v-col class="d-flex justify-center" cols="1">
        <v-divider vertical />
      </v-col>
      <v-col>
        <h3>
          {{ $t('host.controlAccess.servicesAccess') }}
        </h3>
        <p>
          {{
            host.suspended
              ? $t('host.controlAccess.hostsCantReceiveServices')
              : $t('host.controlAccess.hostReceiveServices')
          }}
        </p>
        <v-btn
          @click="openedForm = true"
          :loading="isLoadingEnableServices"
          :color="host.suspended ? 'primary' : 'warning'"
          elevation="0"
        >
          <div style="transform: scale(0.85); opacity: 0.75">
            <v-icon v-if="host.suspended" left> mdi-check </v-icon>
            <v-icon v-else left> mdi-close </v-icon>
          </div>
          {{
            host.suspended
              ? $t('host.controlAccess.enableServices')
              : $t('host.controlAccess.disableServices')
          }}
        </v-btn>
      </v-col>
    </v-row>

    <v-form v-if="openedForm" ref="suspendForm" v-model="valid">
      <v-spacer class="my-6" />

      <v-row>
        <v-col v-if="!host.suspended">
          <HAutocomplete
            v-model="type"
            label="Motivo"
            :items="suspendReasons"
            :item-text="'name'"
            :item-value="'value'"
            :rules="required"
          />
        </v-col>
      </v-row>

      <HTextInput
        v-model="notes"
        :rules="validSize"
        :label="$t('host.controlAccess.form.observations')"
        textarea
        counter
      />

      <div class="d-flex justify-end">
        <v-btn @click="openedForm = false" color="error" class="mr-3" text>
          {{ $t('host.controlAccess.cancel') }}
        </v-btn>
        <v-btn
          v-if="host.suspended"
          :loading="isLoading"
          @click="unsuspend"
          :disabled="!valid"
          elevation="0"
          color="primary"
        >
          {{ $t('host.controlAccess.enableServices') }}
        </v-btn>
        <v-btn
          :disabled="!valid"
          :loading="isLoading"
          v-else
          @click="suspend"
          elevation="0"
          color="primary"
        >
          {{ $t('host.controlAccess.disableServices') }}
        </v-btn>
      </div>
    </v-form>

    <v-divider class="mt-8 mb-10" v-if="hostAccessControlHistory" />

    <h3 class="mb-3">
      {{ $t('host.controlAccess.title') }}
    </h3>

    <div v-if="hostAccessControlHistory">
      <v-data-table
        :no-data-text="$t('appointment.table.noData')"
        :loading-text="$t('appointment.table.loading')"
        :items-per-page="10"
        :footer-props="{
          'items-per-page-text': '',
          'items-per-page-options': [],
        }"
        :items="hostAccessControlHistory"
        :loading="isLoading"
        :headers="headers"
        mobile-breakpoint="20"
        class="elevation-0"
        item-key="date"
        single-expand
        show-expand
      >
        <template v-slot:[`item.date`]="{ item }">
          {{ item.date ? formatDate(item.date) : '---' }}
        </template>
        <template v-slot:[`item.expired_at`]="{ item }">
          {{ item.expired_at ? formatDate(item.expired_at) : '---' }}
        </template>
        <template v-slot:[`item.type`]="{ item }">
          {{
            item.type
              ? $t(`host.controlAccess.suspensionType.${item.type}`)
              : '---'
          }}
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0 ma-0">
            <v-card color="grey lighten-4" class="pa-5" elevation="0" tile>
              <h6 class="text-uppercase grey--text">
                {{ $t('host.controlAccess.form.reason') }}
              </h6>
              <p class="body-2">
                {{
                  item.reason
                    ? suspendReasons.find((it) => it.value == item.reason).name
                    : '---'
                }}
              </p>

              <h6 class="text-uppercase grey--text">
                {{ $t('host.controlAccess.suspendReason') }}
              </h6>
              <p class="body-2 mb-0">
                {{ item.notes ? item.notes : '---' }}
              </p>

              <v-card
                v-if="item.unsuspended_by"
                class="rounded pa-5 mt-5"
                color="green lighten-4"
                elevation="0"
              >
                <h6 class="text-uppercase green--text">
                  {{ $t('host.controlAccess.unsuspendBy') }}
                </h6>
                <p class="body-2 green--text text--darken-3">
                  {{
                    item.unsuspended_by.name ? item.unsuspended_by.name : '---'
                  }}
                </p>

                <h6 class="text-uppercase green--text">
                  {{ $t('host.controlAccess.unsuspendReason') }}
                </h6>
                <p class="body-2 green--text text--darken-3 mb-0">
                  {{ item.unsuspend_notes ? item.unsuspend_notes : '---' }}
                </p>
              </v-card>
            </v-card>
          </td>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import HTextInput from '@/components/app/HTextInput'
import HAutocomplete from '@/components/app/HAutocomplete'
import { catalogPhone, catalogDate } from '@/util/catalog'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { formatDocument } from '@/util/document'
import { unmaskText } from '../../util/helpers'

export default {
  name: 'HostAccessControl',

  components: {
    HTextInput,
    HAutocomplete,
  },

  props: {
    accessControlHistory: Array,
    hostId: String,
  },

  data() {
    return {
      formatDocument,
      isLoadingEnableServices: false,
      valid: false,
      required: [(v) => !!v || this.$t('rules.required')],
      validSize: [
        (v) => !!v || this.$t('rules.required'),
        (v) => v.length >= 10 || 'O campo precisa ter no mínimo 10 caracteres',
        (v) => v.length <= 500 || this.$t('dashboard.alert.errors.maximumCharacters'),
      ],
      openedForm: false,
      suspendReasons: [
        { value: 'ADMIN', name: this.$t('host.suspensionTypes.ADMIN') },
        { value: 'BEHAVIOUR', name: this.$t('host.suspensionTypes.BEHAVIOUR') },
        {
          value: 'INACTIVITY',
          name: this.$t('host.suspensionTypes.INACTIVITY'),
        },
        { value: 'SCORE', name: this.$t('host.suspensionTypes.SCORE') },
        { value: 'RECYCLE', name: this.$t('host.suspensionTypes.RECYCLE') },
        { value: 'QUITTING', name: this.$t('host.suspensionTypes.QUITTING') },
        {
          value: 'ONBOARDING',
          name: this.$t('host.suspensionTypes.ONBOARDING'),
        },
      ],
      headers: [
        {
          text: this.$t('host.controlAccess.headers.date'),
          value: 'date',
          width: '155px',
          sortable: false,
        },
        {
          text: this.$t('host.controlAccess.headers.author'),
          value: 'suspended_by.name',
          width: '200px',
          sortable: false,
        },
        {
          text: this.$t('host.controlAccess.headers.expiredAt'),
          value: 'expired_at',
          width: '155px',
          sortable: false,
        },
        {
          text: this.$t('host.controlAccess.headers.action'),
          value: 'type',
          width: '100px',
          sortable: false,
        },
      ],
      notes: '',
      type: '',
      isHostEnabled: false,
    }
  },

  computed: {
    ...mapGetters([
      'hostAccessControlHistory',
      'host',
      'isLoading',
      'isLoadingChangeStatus',
    ]),
  },

  methods: {
    ...mapActions([
      'suspendHost',
      'unsuspendHost',
      'showSuccessMessage',
      'showErrorMessage',
      'fetchHost',
      'fetchHosts',
      'fetchHostAccessControlHistory',
      'blockHost',
      'unblockHost',
    ]),

    ...mapMutations(['setIsLoading', 'setIsLoadingChangeStatus']),

    formatPhone: catalogPhone,
    formatDate: catalogDate,

    suspend() {
      this.setIsLoading(true)
      this.isLoadingEnableServices = true
      this.suspendHost({
        type: this.type,
        notes: this.notes,
        user_id: this.host.id,
      })
        .then(async () => {
          this.resetForm()
          await this.fetchHost(this.hostId)
          this.maskFields()
          await this.fetchHostAccessControlHistory(this.hostId)
          await this.fetchHosts()
          this.showSuccessMessage('Host suspenso com sucesso!')
        })
        .catch(({ data }) => {
          data?.errors && data?.errors?.length >= 1
            ? this.showErrorMessage(data.errors[0].message)
            : this.showErrorMessage(data.message)
        })
        .finally(() => {
          this.isLoadingEnableServices = false
          this.setIsLoading(false)
        })
    },

    unsuspend() {
      this.setIsLoading(true)
      this.isLoadingEnableServices = true
      this.unsuspendHost({
        notes: this.notes,
        user_id: this.host.id,
      })
        .then(async () => {
          this.resetForm()
          await this.fetchHost(this.hostId)
          this.maskFields()
          await this.fetchHostAccessControlHistory(this.hostId)
          await this.fetchHosts()
          this.showSuccessMessage('Host reabilitado com sucesso!')
        })
        .catch(({ data }) => {
          data?.errors && data?.errors?.length >= 1
            ? this.showErrorMessage(data.errors[0].message)
            : this.showErrorMessage(data.message)
        })
        .finally(() => {
          this.setIsLoading(false)
          this.isLoadingEnableServices = false
        })
    },

    block() {
      this.setIsLoading(true)
      this.isLoadingEnableServices = true
      this.blockHost({
        user_id: this.host.id,
      })
        .then(async () => {
          this.resetForm()
          await this.fetchHost(this.hostId)
          this.maskFields()
          await this.fetchHostAccessControlHistory(this.hostId)
          await this.fetchHosts()
          this.showSuccessMessage('Host bloqueado com sucesso!')
        })
        .catch(({ data }) => {
          data?.errors && data?.errors?.length >= 1
            ? this.showErrorMessage(data.errors[0].message)
            : this.showErrorMessage(data.message)
        })
        .finally(() => {
          this.isLoadingEnableServices = false
          this.setIsLoading(false)
        })
    },

    unblock() {
      this.setIsLoading(true)
      this.isLoadingEnableServices = true
      this.unblockHost({
        user_id: this.host.id,
      })
        .then(async () => {
          this.resetForm()
          await this.fetchHost(this.hostId)
          this.maskFields()
          await this.fetchHostAccessControlHistory(this.hostId)
          await this.fetchHosts()
          this.showSuccessMessage('Host desbloqueado com sucesso!')
        })
        .catch(({ data }) => {
          data?.errors && data?.errors?.length >= 1
            ? this.showErrorMessage(data.errors[0].message)
            : this.showErrorMessage(data.message)
        })
        .finally(() => {
          this.setIsLoading(false)
          this.isLoadingEnableServices = false
        })
    },

    maskFields() {
      this.host.phone = unmaskText(this.host.phone)
      this.host.phone = this.formatPhone(this.host.phone)

      if (this.host.cpf) {
        this.host.cpf = unmaskText(this.host.cpf)
        this.host.cpf = this.formatDocument(this.host.cpf)
      }
    },

    changeHostStatus() {
      this.host.enabled ? this.block() : this.unblock()
    },

    resetForm() {
      this.openedForm = false
      this.notes = ''
      this.reason = ''
      this.type = ''
    },
  },

  mounted() {
    Promise.resolve(this.host).then(() => {
      this.isHostEnabled = this.host.enabled
    })
  },
}
</script>
